/**
 * Service bundles in the customer portal (tracker item 85): paths, wording and the failure texts of a
 * bundle request and of the auto-renew switch. Pure helpers, shared by the request form and the programs page.
 */
import axios from 'axios';
import * as types from '../../api/types';
import { getErrorCode } from '../../utils/apiError';
import { RequestFailure, describeRequestFailure } from '../../utils/portal';

export const PORTAL_PROGRAMS_PATH = '/portal/programs';

/** `Day 45`: a visit's place in the program, counted from the first visit (Day 0). */
export function programDayLabel(offsetDays: number): string {
  return `Day ${offsetDays}`;
}

/** What a customer reads where a price may be missing (an unlinked login, or a price that cannot be worked out yet). */
export const PRICED_ON_APPROVAL = 'Priced on approval';

export function priceOrApproval(price: string | null | undefined, format: (value: string) => string): string {
  return price === null || price === undefined ? PRICED_ON_APPROVAL : format(price);
}

export const VISIT_STATUS_LABELS: Record<types.PortalVisitStatus, string> = {
  REQUESTED: 'Requested',
  PLANNED: 'Planned',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export function visitStatusClass(status: types.PortalVisitStatus): string {
  switch (status) {
    case 'COMPLETED': return 'bg-green-100 text-green-800';
    case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
    case 'SCHEDULED': return 'bg-blue-50 text-blue-800';
    case 'CANCELLED': return 'bg-gray-100 text-gray-600';
    case 'REQUESTED': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-700';
  }
}

/** The program's own status as the customer reads it (a declined request is shown as cancelled). */
export const PROGRAM_STATUS_LABELS: Record<types.EnrollmentStatus, string> = {
  PENDING_APPROVAL: 'Waiting for approval',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export function programStatusClass(status: types.EnrollmentStatus): string {
  switch (status) {
    case 'ACTIVE': return 'bg-green-100 text-green-800';
    case 'PENDING_APPROVAL': return 'bg-yellow-100 text-yellow-800';
    case 'CANCELLED': return 'bg-gray-100 text-gray-700';
    default: return 'bg-blue-100 text-blue-800';
  }
}

/** The line under a program's name that says where it stands. */
export function programStatusNote(enrollment: Pick<types.PortalEnrollment, 'status'>, companyName: string): string {
  switch (enrollment.status) {
    case 'PENDING_APPROVAL': return `${companyName} is reviewing your request. We will confirm the dates and schedule the visits.`;
    case 'CANCELLED': return 'This program is no longer running.';
    case 'COMPLETED': return 'All visits are done.';
    default: return '';
  }
}

/** Why the customer's bundle request failed, in plain words. */
export function describeBundleRequestFailure(error: unknown, context: { company?: string } = {}): RequestFailure {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const code = getErrorCode(error);
    if (status === 403 && code === 'PORTAL_NOT_LINKED') {
      return { kind: 'not-linked', message: "We're still matching your account to your customer record. You can request a program as soon as that is done." };
    }
    if (status === 409 && code === 'BUNDLE_REQUEST_EXISTS') {
      return { kind: 'conflict', message: `You already asked for this program for this property. ${context.company || 'The office'} will be in touch.` };
    }
    if (status === 400) {
      const message = (error.response?.data as { message?: unknown } | undefined)?.message;
      if (typeof message === 'string' && /past/i.test(message)) return { kind: 'validation', message: 'Choose a start date that is today or later.' };
    }
  }
  return describeRequestFailure(error, { company: context.company });
}

/** The auto-renew switch's failure. A cancelled program cannot be changed any more. */
export function describeAutoRenewFailure(error: unknown): RequestFailure {
  if (axios.isAxiosError(error) && error.response?.status === 409 && getErrorCode(error) === 'ENROLLMENT_CANCELLED') {
    return { kind: 'conflict', message: 'This program was cancelled, so the setting can no longer be changed.', reload: true };
  }
  return describeRequestFailure(error);
}

export interface BundleRequestForm {
  propertyId: string;
  startDate: string;
}

/** Problems with the request before it is sent (the server checks again). */
export function bundleRequestProblems(form: BundleRequestForm, today: string): Partial<Record<keyof BundleRequestForm, string>> {
  const problems: Partial<Record<keyof BundleRequestForm, string>> = {};
  if (!form.propertyId) problems.propertyId = 'Choose the property';
  if (!form.startDate) problems.startDate = 'Choose the date you would like the program to start';
  else if (form.startDate < today) problems.startDate = 'Choose a start date that is today or later';
  return problems;
}

/** `3 visits over 90 days` */
export function programSpan(visitCount: number, spanDays: number): string {
  return `${visitCount} visit${visitCount === 1 ? '' : 's'}${visitCount > 1 ? ` over ${spanDays} day${spanDays === 1 ? '' : 's'}` : ''}`;
}
